'use client';

import type { IChallengeClip } from '@repo/sdk';
import { Badge, Checkbox, TableCell, TableRow } from '@repo/ui';
import type { JSX } from 'react';
import { CropRowActions } from './crop-row-actions';
import { CropStatusBadge } from './crop-status-badge';

interface ICropTableRowProps {
  clip: IChallengeClip;
  isSelected: boolean;
  isCropping: boolean;
  onToggle: (id: number) => void;
  onCrop: (clip: IChallengeClip) => void;
  onPreview: (clip: IChallengeClip) => void;
}

function formatMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function CropTableRow({
  clip,
  isSelected,
  isCropping,
  onToggle,
  onCrop,
  onPreview,
}: ICropTableRowProps): JSX.Element {
  return (
    <TableRow data-state={isSelected ? 'selected' : undefined}>
      <TableCell>
        <Checkbox checked={isSelected} onCheckedChange={() => onToggle(clip.id)} />
      </TableCell>
      <TableCell className="font-semibold">{clip.challengeTitle ?? '-'}</TableCell>
      <TableCell className="max-w-48 truncate text-muted-foreground text-xs">
        {clip.collectionPath ?? '-'}
      </TableCell>
      <TableCell className="font-mono text-xs">
        {clip.startMs != null && clip.endMs != null
          ? `${formatMs(clip.startMs)} - ${formatMs(clip.endMs)}`
          : '-'}
      </TableCell>
      <TableCell className="font-mono text-xs">
        {clip.startMs != null && clip.endMs != null ? formatMs(clip.endMs - clip.startMs) : '-'}
      </TableCell>
      <TableCell>
        {clip.cropStatus ? (
          <CropStatusBadge status={clip.cropStatus} />
        ) : (
          <Badge variant="secondary">manual</Badge>
        )}
      </TableCell>
      <TableCell className="max-w-32 truncate text-xs text-destructive">
        {clip.errorMessage ?? ''}
      </TableCell>
      <TableCell className="text-right">
        <CropRowActions clip={clip} isCropping={isCropping} onCrop={onCrop} onPreview={onPreview} />
      </TableCell>
    </TableRow>
  );
}
